import { useQuery } from '@tanstack/react-query'
import Challenge from '../../model/Challenge'
import { dataSource } from '../../service/domain/_service.config'
import { useUser } from '../user/useUser'

export const CHALLENGES_QUERY_KEY = ['challenges'] as const

function challengeIsVisible(
  challenge: Pick<Challenge, 'isTest'> | undefined,
  isAdmin: boolean,
): boolean {
  if (isAdmin) return true
  if (!challenge) return true
  return !challenge.isTest
}

function useChallengeCatalog() {
  return useQuery({
    queryKey: CHALLENGES_QUERY_KEY,
    queryFn: () => dataSource.getChallenges(),
  })
}

/** Catalog plus whether this user is allowed to open a challenge id. */
export function useChallengeVisibility() {
  const catalog = useChallengeCatalog()
  const { data: user, isFetched } = useUser()
  const ready = isFetched && (catalog.isSuccess || catalog.isError)
  const isAdmin = user?.isAdmin === true

  function canSee(id: string) {
    if (!ready) return false
    if (catalog.isError) return true
    return challengeIsVisible(
      catalog.data?.find((challenge) => challenge.id === id),
      isAdmin,
    )
  }

  return { ready, canSee, challenges: catalog.data, error: catalog.error }
}

export function useChallenges() {
  const { ready, canSee, challenges, error } = useChallengeVisibility()
  return {
    data: ready ? (challenges ?? []).filter((challenge) => canSee(challenge.id)) : undefined,
    isLoading: !ready,
    error,
  }
}
